'use client';

import useEmblaCarousel from 'embla-carousel-react';
import * as React from 'react';

import { DesktopSlide } from '@/components/sections/about/team/image-carousel/desktop-slide';
import { MobileSlide } from '@/components/sections/about/team/image-carousel/mobile-slide';
import Label from '@/components/typography/label';
import { cn } from '@/lib/utils';

type Slide = {
    src: string;
};

type Height = {
    mobile: number;
    desktop: number;
};

type ImageCarouselProps = {
    slides: Slide[];
    height?: Height;
    className?: string;
};

export function ImageCarousel({
    slides,
    height = { mobile: 288, desktop: 670 },
    className,
}: ImageCarouselProps) {
    const [emblaRef, emblaApi] = useEmblaCarousel({
        loop: true,
        align: 'start',
        dragFree: true,
    });

    const [selectedIndex, setSelectedIndex] = React.useState(0);
    const [ratios, setRatios] = React.useState<number[]>(() =>
        new Array(slides.length).fill(NaN),
    );

    const setRatioAt = React.useCallback((idx: number, ratio: number) => {
        setRatios((prev) => {
            if (prev[idx] === ratio) return prev;
            const next = prev.slice();
            next[idx] = ratio;
            return next;
        });
    }, []);

    React.useEffect(() => {
        if (!emblaApi) return;

        const scrollSnaps = emblaApi.scrollSnapList();

        const updateClosest = () => {
            const progress = emblaApi.scrollProgress();
            let closest = 0;
            let minDiff = Infinity;

            scrollSnaps.forEach((snap, i) => {
                const diff = Math.abs(snap - progress);
                if (diff < minDiff) {
                    minDiff = diff;
                    closest = i;
                }
            });

            const maxProgress = 1;
            const nearEnd = progress > maxProgress - 0.01;

            if (nearEnd) {
                setSelectedIndex(slides.length - 1);
            } else {
                setSelectedIndex(closest);
            }
        };

        emblaApi.on('scroll', updateClosest);
        emblaApi.on('resize', updateClosest);
        emblaApi.on('reInit', updateClosest);

        updateClosest();
    }, [emblaApi, slides.length]);

    return (
        <div className="relative w-full">
            <div className={cn('relative overflow-hidden', className)}>
                <div
                    ref={emblaRef}
                    className="overflow-hidden hover:cursor-pointer"
                >
                    <div className="flex gap-4">
                        {slides.map((slide, index) => {
                            const ratio = ratios[index] ?? 1;
                            const widthMobile = Number.isFinite(ratio)
                                ? Math.round(height.mobile * ratio)
                                : height.mobile;
                            const widthDesktop = Number.isFinite(ratio)
                                ? Math.round(height.desktop * ratio)
                                : height.desktop;

                            return (
                                <div
                                    key={index}
                                    className={cn(
                                        'shrink-0',
                                        index === slides.length - 1 && 'mr-4',
                                    )}
                                >
                                    <MobileSlide
                                        slide={slide}
                                        index={index}
                                        width={widthMobile}
                                        height={height.mobile}
                                        setRatioAt={setRatioAt}
                                    />
                                    <DesktopSlide
                                        slide={slide}
                                        index={index}
                                        width={widthDesktop}
                                        height={height.desktop}
                                        setRatioAt={setRatioAt}
                                    />
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>

            <Label className="absolute top-full left-1/2 mt-4 -translate-x-1/2 text-black">
                {(selectedIndex % slides.length) + 1}/{slides.length}
            </Label>
        </div>
    );
}
